/**
 * An icon button that opens a real popover on CLICK.
 *
 * Extracted after `/pr-review` iter 1 (Agent C, I2) pointed out that the stats
 * popover was using the shared hover `Tooltip` for a seven-row numeric table —
 * the exact thing `FullHistory`'s docblock argues against. Hover content is
 * `pointer-events-none`: it cannot be selected or copied, it dismisses the
 * moment the pointer drifts, and it is unreachable on touch. Fine for one line
 * of help text; wrong for anything the reader wants to sit with.
 *
 * It also made a durable fact untestable — `export.spec.ts` asserts that the
 * page still knows an export happened after a reload, and that count had moved
 * inside a hover bubble Playwright can never see.
 *
 * ## What this owns, so its two callers do not each get it half-right
 *
 * - **Escape returns focus to the trigger.** Closing a panel while focus is
 *   inside it otherwise drops focus to `<body>`, which strands a keyboard user
 *   at the top of the document (Agent C, I3).
 * - **Outside-close listens on `pointerdown`, not `click`.** A drag-select that
 *   starts inside the panel and ends outside must not close it mid-selection.
 * - **One tab stop.** The trigger is a real `button`; the panel is not
 *   focusable itself.
 * - `aria-expanded` / `aria-controls` are wired to the panel id, so a screen
 *   reader announces the relationship instead of a bare icon.
 *
 * ## It is a DISCLOSURE, not a dialog
 *
 * The first version announced `aria-haspopup="dialog"` while the panel was a
 * bare `<div>` — no `role`, no accessible name, and focus never moved into it.
 * A screen-reader user was told a dialog had opened and then found nothing
 * named and nothing focused; the only way in was a blind Tab. Worse, the test
 * suite PINNED `aria-haspopup === "dialog"`, so it locked the mismatch in
 * rather than catching it (`/pr-review` iter 2, Agent C N1).
 *
 * Both callers show a short read-only panel that the reader dismisses and moves
 * on from. That is a disclosure widget, and the honest wiring is
 * `aria-expanded` + `aria-controls` with a NAMED region — no `haspopup`, no
 * focus trap, no forced focus move. Making it a real dialog would mean moving
 * focus in, trapping it, and restoring on close, which is a heavier contract
 * than either caller wants and heavier than the content justifies.
 *
 * Listeners are bound only while open — a closed popover costs a page that
 * already mounts several of these nothing.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN_PX = 8;
const PANEL_GAP_PX = 4;
/** Never squeeze the panel below this, even with no room either side. */
const MIN_PANEL_HEIGHT_PX = 120;

export function IconPopover({
  icon,
  label,
  triggerTestId,
  panelTestId,
  panelClassName = "",
  children,
}: {
  icon: React.ReactNode;
  /** Accessible name for the trigger AND for the panel region it opens. */
  label: string;
  triggerTestId: string;
  panelTestId: string;
  panelClassName?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();
  const [coords, setCoords] = useState<{ top: number; left: number; maxHeight: number } | null>(
    null
  );

  // The panel is portalled to <body> and positioned against the viewport. Inline
  // (`absolute`) it lived inside the page's stacking contexts, so the contacts
  // table's sticky header (z-20) and frozen cells (z-30) painted over it and hid
  // rows of "Full history" (F5). A portal escapes every ancestor stacking
  // context and overflow clip; the position is dynamic, hence the inline style.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = panelRef.current?.offsetWidth ?? 0;
      // The content's natural height, not `offsetHeight`: once placed, the
      // panel's own `maxHeight` caps `offsetHeight`, and a re-place (resize,
      // scroll) would then read the cap and never flip (/pr-review iter 6 on
      // PR #993, Agent C I6-3).
      const height = panelRef.current?.scrollHeight ?? 0;
      const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
      // A `fixed` panel does not extend the page's scroll height the way the
      // old `absolute` one did, so one that runs past the viewport's bottom
      // edge cannot be scrolled into view (/pr-review iter 5 on PR #993,
      // Agent A). Below the trigger when it fits or has the room; otherwise
      // above it. Either way it is capped to the room it has and scrolls
      // inside itself.
      const below = window.innerHeight - rect.bottom - PANEL_GAP_PX - VIEWPORT_MARGIN_PX;
      const above = rect.top - PANEL_GAP_PX - VIEWPORT_MARGIN_PX;
      const flip = height > below && above > below;
      const maxHeight = Math.max(flip ? above : below, MIN_PANEL_HEIGHT_PX);
      setCoords({
        top: flip
          ? Math.max(VIEWPORT_MARGIN_PX, rect.top - PANEL_GAP_PX - Math.min(height, maxHeight))
          : rect.bottom + PANEL_GAP_PX,
        left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)),
        maxHeight,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Escape is document-global, so an Escape meant for a MODAL layered above
      // should not also close a popover underneath it. But the first version of
      // this guard bailed whenever focus was anywhere outside the popover,
      // which made Escape a dead end for keyboard users: tab out of the panel
      // to anything else on the page and Escape stopped working entirely
      // (`/pr-review` iter 3 §3b, iter 4 §2(c)).
      //
      // So it is scoped to what it was actually protecting against — a dialog
      // or modal — and Escape closes from everywhere else, which is what the
      // key is for.
      const active = document.activeElement;
      const focusIsInAModalAbove =
        active != null &&
        !rootRef.current?.contains(active) &&
        !panelRef.current?.contains(active) &&
        active.closest("[role='dialog'],[aria-modal='true']") != null;
      if (focusIsInAModalAbove) return;
      setOpen(false);
      // Focus goes back where it came from. Without this, dismissing a panel
      // you had tabbed into leaves focus on `<body>`.
      triggerRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        data-testid={triggerTestId}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={`rounded-sm p-1 hover:text-text focus:outline-none focus:ring-1 focus:ring-accent ${
          open ? "text-text" : "text-text-muted"
        }`}
      >
        {icon}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            data-testid={panelTestId}
            // NAMED region: without this the panel is an anonymous `<div>` and a
            // screen-reader user who tabs in has no idea what they are in.
            //
            // biome-ignore lint/a11y/useSemanticElements: the rule's answer for
            // `role="group"` is `<fieldset>`, which is wrong here — a fieldset
            // groups FORM CONTROLS and drags along a legend contract, and both
            // callers render read-only content (a stats table, a lineage trail).
            // `role="group"` with an `aria-label` is the accurate description of
            // "a named set of related elements", so the role stays and the rule
            // is suppressed with its reason, in the same voice as the
            // `noControlCharactersInRegex` suppression in `importTitle.ts`.
            role="group"
            aria-label={label}
            style={{ top: coords?.top ?? 0, left: coords?.left ?? 0, maxHeight: coords?.maxHeight }}
            className={`fixed z-50 w-max overflow-auto rounded-md border border-border bg-surface p-3 shadow-lg ${coords ? "" : "invisible"} ${panelClassName}`}
          >
            {children}
          </div>,
          document.body
        )}
    </div>
  );
}
